import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Columns3,
  RotateCcw,
} from "lucide-react";

import api from "@/lib/api";
import { formatDate } from "@/lib/date";
import {
  STATUS_FILTER_OPTIONS,
  PRIORITIES,
  PRIORITY_LABEL,
} from "@/lib/constants";

import StatusBadge from "@/components/common/StatusBadge";
import PriorityBadge from "@/components/common/PriorityBadge";
import SLATimer from "@/components/common/SLATimer";
import DateRangeFilter from "@/components/tickets/DateRangeFilter";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";


const TICKET_COLUMNS = [
  {
    id: "ticket_number",
    label: "Ticket Number",
    required: true,
  },
  {
    id: "title",
    label: "Title",
    required: true,
  },
  {
    id: "status",
    label: "Status",
  },
  {
    id: "priority",
    label: "Priority",
  },
  {
    id: "application",
    label: "Application",
  },
  {
    id: "sla",
    label: "SLA",
  },
  {
    id: "created",
    label: "Created",
  },
];

const DEFAULT_VISIBLE_COLUMNS =
  TICKET_COLUMNS.map((column) => column.id);


export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useSearchParams();
  const location = useLocation();

  const [q, setQ] = useState(
    search.get("q") || ""
  );

  const [debouncedQ, setDebouncedQ] = useState(
    search.get("q") || ""
  );

  const [status, setStatus] = useState(
    search.get("status") || ""
  );

  const [priority, setPriority] = useState(
    search.get("priority") || ""
  );

  const [fromDate, setFromDate] = useState(
    search.get("from_date") || ""
  );

  const [toDate, setToDate] = useState(
    search.get("to_date") || ""
  );

  const [refreshKey, setRefreshKey] = useState(0);

   const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [visibleColumns, setVisibleColumns] =
    useState(() => {
      try {
        const saved = window.localStorage.getItem(
          "helpdesk-ticket-columns"
        );

        if (!saved) {
          return DEFAULT_VISIBLE_COLUMNS;
        }

        const parsed = JSON.parse(saved);

        if (!Array.isArray(parsed)) {
          return DEFAULT_VISIBLE_COLUMNS;
        }

        const valid = DEFAULT_VISIBLE_COLUMNS.filter(
          (column) => parsed.includes(column)
        );

        const required = TICKET_COLUMNS
          .filter((column) => column.required)
          .map((column) => column.id);

        return Array.from(
          new Set([...required, ...valid])
        );
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    });


  // ----------------------------------------------------------
  // URL → page filters
  // ----------------------------------------------------------

  useEffect(() => {
    const nextQ = search.get("q") || "";
    const nextStatus = search.get("status") || "";
    const nextPriority = search.get("priority") || "";
    const nextFromDate = search.get("from_date") || "";
    const nextToDate = search.get("to_date") || "";

    setQ((current) =>
      current === nextQ ? current : nextQ
    );

    // Skip the debounce so a stale search term is not written back
    // into the URL after navigation (e.g. clicking Tickets in the sidebar).
    setDebouncedQ((current) =>
      current === nextQ ? current : nextQ
    );

    setStatus((current) =>
      current === nextStatus ? current : nextStatus
    );

    setPriority((current) =>
      current === nextPriority
        ? current
        : nextPriority
    );

    setFromDate((current) =>
      current === nextFromDate
        ? current
        : nextFromDate
    );

    setToDate((current) =>
      current === nextToDate
        ? current
        : nextToDate
    );
  }, [search]);


  // ----------------------------------------------------------
  // Refresh when tickets change elsewhere
  // ----------------------------------------------------------

  useEffect(() => {
    const refresh = () => {
      setRefreshKey((current) => current + 1);
    };

    window.addEventListener(
      "helpdesk-ticket-changed",
      refresh
    );

    window.addEventListener(
      "focus",
      refresh
    );

    return () => {
      window.removeEventListener(
        "helpdesk-ticket-changed",
        refresh
      );

      window.removeEventListener(
        "focus",
        refresh
      );
    };
  }, []);


  // ----------------------------------------------------------
  // Search debounce
  // ----------------------------------------------------------

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [q]);


  // ----------------------------------------------------------
  // Load tickets
  // ----------------------------------------------------------

  useEffect(() => {
    setLoading(true);

    const params = {
      _ts: Date.now(),
    };

    const urlQ = search.get("q") || "";
    const urlStatus = search.get("status") || "";
    const urlPriority = search.get("priority") || "";
    const urlFromDate = search.get("from_date") || "";
    const urlToDate = search.get("to_date") || "";

    if (urlQ) {
      params.q = urlQ;
    }

    if (urlStatus) {
      params.status = urlStatus;
    }

    if (urlPriority) {
      params.priority = urlPriority;
    }

    if (urlFromDate) {
      params.from_date = urlFromDate;
    }

    if (urlToDate) {
      params.to_date = urlToDate;
    }

    const passthrough = [
      "open",
      "sla",
      "assigned",
      "unassigned",
      "assigned_engineer_id",
      "raised_by",
      "application_id",
    ];

    passthrough.forEach((key) => {
      const value = search.get(key);

      if (value) {
        params[key] = value;
      }
    });

    // Ignore a response that arrives after the filters have changed again.
    let stale = false;

    api
      .get("/tickets", { params })
      .then((response) => {
        if (!stale) setTickets(response.data || []);
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });

    return () => {
      stale = true;
    };
  }, [
    search,
    refreshKey,
  ]);


  // ----------------------------------------------------------
  // Page filters → URL
  //
  // Runs only when a filter changes, never when the URL changes;
  // otherwise navigating to /tickets would immediately write the
  // previous filters back and the two syncs would fight (flicker).
  // ----------------------------------------------------------

  useEffect(() => {
    const next = new URLSearchParams(search);

    if (debouncedQ) {
      next.set("q", debouncedQ);
    } else {
      next.delete("q");
    }

    if (status) {
      next.set("status", status);
    } else {
      next.delete("status");
    }

    if (priority) {
      next.set("priority", priority);
    } else {
      next.delete("priority");
    }

    if (fromDate) {
      next.set("from_date", fromDate);
    } else {
      next.delete("from_date");
    }

    if (toDate) {
      next.set("to_date", toDate);
    } else {
      next.delete("to_date");
    }

    if (next.toString() !== search.toString()) {
      setSearch(next, {
        replace: true,
      });
    }
    // `search` is deliberately not a dependency (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedQ,
    status,
    priority,
    fromDate,
    toDate,
  ]);


  // ----------------------------------------------------------
  // Column preference
  // ----------------------------------------------------------

  useEffect(() => {
    window.localStorage.setItem(
      "helpdesk-ticket-columns",
      JSON.stringify(visibleColumns)
    );
  }, [visibleColumns]);


  // ----------------------------------------------------------
  // Reset pagination when filters change
  // ----------------------------------------------------------

  useEffect(() => {
    setPage(1);
  }, [
    debouncedQ,
    status,
    priority,
    fromDate,
    toDate,
    pageSize,
  ]);


  // ----------------------------------------------------------
  // Pagination
  // ----------------------------------------------------------

  const totalPages = Math.max(
    1,
    Math.ceil(tickets.length / pageSize)
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [
    page,
    totalPages,
  ]);


  const paginatedTickets = useMemo(() => {
    const start =
      (page - 1) * pageSize;

    return tickets.slice(
      start,
      start + pageSize
    );
  }, [
    tickets,
    page,
    pageSize,
  ]);


  const firstResult =
    tickets.length === 0
      ? 0
      : (page - 1) * pageSize + 1;

  const lastResult =
    tickets.length === 0
      ? 0
      : Math.min(
          page * pageSize,
          tickets.length
        );


  // ----------------------------------------------------------
  // Column customization
  // ----------------------------------------------------------

  const isColumnVisible = (id) =>
    visibleColumns.includes(id);


  const toggleColumn = (id) => {
    const column = TICKET_COLUMNS.find(
      (item) => item.id === id
    );

    if (column?.required) {
      return;
    }

    setVisibleColumns((current) => {
      if (current.includes(id)) {
        return current.filter(
          (item) => item !== id
        );
      }

      return DEFAULT_VISIBLE_COLUMNS.filter(
        (item) =>
          current.includes(item) ||
          item === id
      );
    });
  };


  return (
    <div
      className="space-y-5"
      data-testid="tickets-page"
    >

      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>

          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">
                Dashboard
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbPage>
              Tickets
            </BreadcrumbPage>
          </BreadcrumbItem>

        </BreadcrumbList>
      </Breadcrumb>


      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Ticket queue
          </div>

          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            All Tickets
          </h1>

          <p className="mt-1 text-xs text-muted-foreground">
            Latest tickets appear first.
          </p>
        </div>

        <div className="text-xs text-muted-foreground">
          {tickets.length} result
          {tickets.length === 1 ? "" : "s"}
        </div>

      </div>


      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card/60 p-3">

        <Input
          data-testid="tickets-search"
          value={q}
          onChange={(event) =>
            setQ(event.target.value)
          }
          placeholder="Search by number, title, description…"
          className="min-w-[240px] max-w-sm flex-1 bg-secondary/60"
        />


        <Select
          value={status}
          onValueChange={(value) =>
            setStatus(
              value === "__all__"
                ? ""
                : value
            )
          }
        >
          <SelectTrigger
            data-testid="tickets-status-filter"
            className="w-52"
          >
            <SelectValue placeholder="All Status" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="__all__">
              All Status
            </SelectItem>

            {STATUS_FILTER_OPTIONS.map(
              (option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>


        <Select
          value={priority}
          onValueChange={(value) =>
            setPriority(
              value === "__all__"
                ? ""
                : value
            )
          }
        >
          <SelectTrigger
            data-testid="tickets-priority-filter"
            className="w-40"
          >
            <SelectValue placeholder="Any Priority" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="__all__">
              All Priorities
            </SelectItem>

            {PRIORITIES.map(
              (ticketPriority) => (
                <SelectItem
                  key={ticketPriority}
                  value={ticketPriority}
                >
                  {
                    PRIORITY_LABEL[
                      ticketPriority
                    ]
                  }
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>


        {/* Created date range */}
        <DateRangeFilter
          from={fromDate}
          to={toDate}
          onChange={(from, to) => {
            setFromDate(from);
            setToDate(to);
          }}
        />


        {/* Column customization */}
        <Popover>

          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="ml-auto gap-2"
              data-testid="ticket-column-selector"
            >
              <Columns3 className="h-4 w-4" />
              Columns
            </Button>
          </PopoverTrigger>

          <PopoverContent
            align="end"
            className="w-64"
          >
            <div className="mb-3 flex items-center justify-between">

              <div>
                <div className="text-sm font-semibold">
                  Table Columns
                </div>

                <div className="text-[11px] text-muted-foreground">
                  Choose which columns are visible.
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                type="button"
                title="Reset Columns"
                aria-label="Reset Columns"
                onClick={() =>
                  setVisibleColumns(
                    DEFAULT_VISIBLE_COLUMNS
                  )
                }
              >
                <RotateCcw className="h-4 w-4" />
              </Button>

            </div>


            <div className="space-y-1">

              {TICKET_COLUMNS.map(
                (column) => (
                  <label
                    key={column.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-secondary/60"
                  >

                    <Checkbox
                      checked={
                        visibleColumns.includes(
                          column.id
                        )
                      }
                      disabled={column.required}
                      onCheckedChange={() =>
                        toggleColumn(column.id)
                      }
                    />

                    <span className="text-sm">
                      {column.label}
                    </span>

                    {column.required && (
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        Required
                      </span>
                    )}

                  </label>
                )
              )}

            </div>
          </PopoverContent>
        </Popover>

      </div>


      {/* Tickets table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card/70">

        <div className="overflow-x-auto">

          <table className="w-full min-w-[850px] text-sm">

            <thead className="bg-secondary/25 text-[11px] uppercase tracking-widest text-muted-foreground">

              <tr className="border-b border-border">

                {isColumnVisible(
                  "ticket_number"
                ) && (
                  <th className="px-4 py-3 text-left">
                    Ticket Number
                  </th>
                )}

                {isColumnVisible(
                  "title"
                ) && (
                  <th className="px-4 py-3 text-left">
                    Title
                  </th>
                )}

                {isColumnVisible(
                  "status"
                ) && (
                  <th className="px-4 py-3 text-left">
                    Status
                  </th>
                )}

                {isColumnVisible(
                  "priority"
                ) && (
                  <th className="px-4 py-3 text-left">
                    Priority
                  </th>
                )}

                {isColumnVisible(
                  "application"
                ) && (
                  <th className="px-4 py-3 text-left">
                    Application
                  </th>
                )}

                {isColumnVisible(
                  "sla"
                ) && (
                  <th className="px-4 py-3 text-left">
                    SLA
                  </th>
                )}

                {isColumnVisible(
                  "created"
                ) && (
                  <th className="px-4 py-3 text-left">
                    Created
                  </th>
                )}

              </tr>
            </thead>


            <tbody>

              {loading && tickets.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      visibleColumns.length
                    }
                    className="px-4 py-10 text-center text-sm text-muted-foreground"
                  >
                    Loading tickets…
                  </td>
                </tr>
              )}


              {!loading &&
                tickets.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        visibleColumns.length
                      }
                      className="px-4 py-12 text-center"
                    >

                      <div className="font-medium text-foreground">
                        No tickets found
                      </div>

                      <div className="mt-1 text-xs text-muted-foreground">
                        Try changing your search or filters.
                      </div>

                    </td>
                  </tr>
                )}


              {paginatedTickets.map(
                  (ticket) => (
                    <tr
                      key={ticket.id}
                      data-row
                      data-testid={`ticket-row-${ticket.ticket_number}`}
                      className="border-b border-border/70 last:border-b-0"
                    >

                      {isColumnVisible(
                        "ticket_number"
                      ) && (
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-muted-foreground">

                          <Link
                            to={`/tickets/${ticket.id}`}
                            className="font-medium hover:text-[#e5c158]"
                          >
                            {
                              ticket.ticket_number
                            }
                          </Link>

                        </td>
                      )}


                      {isColumnVisible(
                        "title"
                      ) && (
                        <td className="min-w-[260px] max-w-md px-4 py-3">

                          <Link
                            to={`/tickets/${ticket.id}`}
                            className="block truncate font-medium hover:text-[#e5c158]"
                            title={ticket.title}
                          >
                            {ticket.title}
                          </Link>

                        </td>
                      )}


                      {isColumnVisible(
                        "status"
                      ) && (
                        <td className="whitespace-nowrap px-4 py-3">
                          <StatusBadge
                            status={
                              ticket.status
                            }
                          />
                        </td>
                      )}


                      {isColumnVisible(
                        "priority"
                      ) && (
                        <td className="whitespace-nowrap px-4 py-3">
                          <PriorityBadge
                            priority={
                              ticket.priority
                            }
                          />
                        </td>
                      )}


                      {isColumnVisible(
                        "application"
                      ) && (
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                          {
                            ticket.application_code ||
                            "—"
                          }
                        </td>
                      )}


                      {isColumnVisible(
                        "sla"
                      ) && (
                        <td className="whitespace-nowrap px-4 py-3">

                          {ticket.resolution_due ? (
                            <SLATimer
                              deadline={
                                ticket.resolution_due
                              }
                              status={
                                ticket.status
                              }
                              completedAt={
                                ticket.closed_at ||
                                ticket.resolved_at
                              }
                              compact
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}

                        </td>
                      )}


                      {isColumnVisible(
                        "created"
                      ) && (
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                          {formatDate(
                            ticket.created_at
                          )}
                        </td>
                      )}

                    </tr>
                  )
                )}

            </tbody>
          </table>

        </div>


        {/* Pagination */}
        {!loading &&
          tickets.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-secondary/10 px-4 py-3">

              <div className="text-xs text-muted-foreground">

                Showing{" "}

                <span className="font-medium text-foreground">
                  {firstResult}
                </span>

                {" – "}

                <span className="font-medium text-foreground">
                  {lastResult}
                </span>

                {" of "}

                <span className="font-medium text-foreground">
                  {tickets.length}
                </span>

              </div>


              <div className="flex flex-wrap items-center gap-4">

                <div className="flex items-center gap-2">

                  <span className="text-xs text-muted-foreground">
                    Rows
                  </span>

                  <Select
                    value={String(
                      pageSize
                    )}
                    onValueChange={(
                      value
                    ) =>
                      setPageSize(
                        Number(value)
                      )
                    }
                  >

                    <SelectTrigger
                      className="h-8 w-[74px]"
                      data-testid="ticket-page-size"
                    >
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="10">
                        10
                      </SelectItem>

                      <SelectItem value="25">
                        25
                      </SelectItem>

                      <SelectItem value="50">
                        50
                      </SelectItem>
                    </SelectContent>

                  </Select>

                </div>


                <div className="whitespace-nowrap text-xs text-muted-foreground">

                  Page{" "}

                  <span className="font-medium text-foreground">
                    {page}
                  </span>

                  {" of "}

                  <span className="font-medium text-foreground">
                    {totalPages}
                  </span>

                </div>


                <div className="flex items-center gap-1">

                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page <= 1}
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.max(
                            1,
                            current - 1
                          )
                      )
                    }
                    aria-label="Previous Page"
                    title="Previous Page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>


                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={
                      page >= totalPages
                    }
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.min(
                            totalPages,
                            current + 1
                          )
                      )
                    }
                    aria-label="Next Page"
                    title="Next Page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>

                </div>
              </div>

            </div>
          )}

      </div>
    </div>
  );
}
